import React, { useState, useEffect, useRef } from 'react';
import { Formik, Field, FormikProps, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { CompanyService } from '$/services';
import BixbyQuillWrapper from '$/components/quill';
import Modal from '$/components/global/modal';
import { toastError } from '$/store/toast/actions';
import BixButton from '$/components/global/bixButton';
import BixCheckbox from '$/components/global/bixCheckbox';
import BixReadonlySelect from '$/components/global/bixReadonlySelect';
import BixReadonlyMultiselect from '$/components/global/bixReadonlyMultiselect';
import ExternalLink from '$/components/global/externalLink';
import InputBlock from '$/components/global/inputBlock';
import TimePickerField from '$/components/timePickerField';
import { updateDraft } from '$/store/draft/actions';
import { setCurrentCompany } from '$/store/company/actions';
import { ReactComponent as LockIcon } from '$/assets/icons/Lock.svg';
import { ReactComponent as UnlockIcon } from '$/assets/icons/Unlock.svg';
import { getIsCurrentCompanyPublicState } from '$/store/company/selectors';
import { DedicatedFinancialsModal } from '$/pages/admin/company/company-detail/DedicatedFinancialsModal';
import CompanyOwnership from '$/pages/admin/company/company-detail/companyOwnership/CompanyOwnership';
import { getIsCurrentUserSuperAdminState, getIsCurrentUserDocHostingAdminState } from '$/store/profile/selectors';
import { PrivateEquitySponsorType } from '$/core/enums';
import './index.scss';
import DatePickerFieldWithHeader from '$/components/datePickerFieldWithHeader';

export interface ICompanyProfileProps {
  draftCompany?: Bixby.Models.DraftCompany;
  isEditing?: boolean;
  discardDraft?: boolean;
  dataSiteHosts?: Bixby.Models.IIdentifiable[];
  dataSiteProviders?: Bixby.Models.IIdentifiable[];
  dataSiteStatuses?: Bixby.Models.IIdentifiable[];
  dataSiteTypes?: Bixby.Models.IIdentifiable[];
  industries?: Bixby.Models.IIdentifiable[];
  fitchIndustries?: Bixby.Models.IIdentifiable[];
  industryGroups?: Bixby.Models.IIdentifiable[];
  sectors?: Bixby.Models.IIdentifiable[];
  transactionTypes?: Bixby.Models.IIdentifiable[];
  currentCompany?: Bixby.Models.AdminCompany;
  isSuperAdmin: boolean;
  isDocHostingAdmin: boolean;
  isCurrentCompanyPublic: boolean;
  setCurrentCompany: (companyId: string) => Promise<void>;
  dispatchUpdateDraftCompany: (companyId: string, updatedCompany: Bixby.Models.JsonPatchOperation[]) => void;
  dispatchToastError: (message: string) => void;
}

export interface CompanyProfileValues {
  name: string;
  announcement: string;
  description: string;
  dataSiteHost_Id: string;
  dataSiteName: string;
  dataSiteProvider_Id: string;
  dataSiteStatus_Id: string;
  dataSiteType_Id: string;
  flagged: boolean;
  industry_Id: string;
  industryGroup_Id: string;
  sector_Id: string;
  websiteUrl: string;
  openFigiTicker: string;
  owners: Array<Bixby.Models.PrivateEquitySponsorsCompany>;
  restrictNewsBackdate: boolean;
  publicStockTicker?: string;
  cikNumber?: string;
  commitmentDate?: Date;
  commitmentDateNotAvailable?: boolean;
  commitmentTime?: Date;
  timezone?: string;
  isDelisted: boolean;
  transactionTypes: Bixby.Models.IIdentifiable[];
  fitchIndustry_Id?: string;
  lfiId?: string;
}

const defaultTimezonesOptions = (Bixby.Constants.DEFAULTTIMEZONES || []).map(tz => {
  return (
    <option value={tz.Id} key={tz.Id} className="default_option">
      {tz.DisplayName}
    </option>
  );
});

const timezoneOptions = (Bixby.Constants.TIMEZONES || []).map(tz => {
  return (
    <option value={tz.Id} key={tz.Id}>
      {tz.DisplayName}
    </option>
  );
});

const CompanyProfile: React.FC<ICompanyProfileProps> = props => {
  const {
    draftCompany,
    isEditing,
    discardDraft,
    dataSiteHosts,
    dataSiteProviders,
    dataSiteStatuses,
    dataSiteTypes,
    industries,
    fitchIndustries,
    industryGroups,
    sectors,
    transactionTypes,
    currentCompany,
    isSuperAdmin,
    dispatchUpdateDraftCompany,
    isDocHostingAdmin,
    setCurrentCompany,
    isCurrentCompanyPublic,
    dispatchToastError,
  } = props;
  const [initialFormValues, setInitialFormValues] = useState({} as CompanyProfileValues);
  const { createdDate } = currentCompany || ({} as Bixby.Models.AdminCompany);
  const [dateCreated, setDateCreated] = useState('');
  const [bixbyId, setBixbyId] = useState('');
  const [documentLibraryWebUrl, setDocumentLibraryWebUrl] = useState('');
  const [watermarkConfirmationModal, setWatermarkConfirmationModal] = useState(false);
  const [showCreateDedicatedFinancialsModal, setShowCreateDedicatedFinancialsModal] = useState(false);

  const formatDate = (d: any) => {
    return Bixby.Utils.isDate(d) ? d.toLocaleDateString() : new Date(d.toString()).toLocaleDateString();
  };

  const formikRef = useRef<FormikProps<CompanyProfileValues>>(null);

  useEffect(() => {
    if (currentCompany && currentCompany.createdDate) {
      setDateCreated(formatDate(createdDate));
    }
    if (currentCompany && currentCompany.bixbyId) {
      setBixbyId(currentCompany.bixbyId);
    }
    if (currentCompany && currentCompany.documentLibraryWebUrl) {
      setDocumentLibraryWebUrl(currentCompany.documentLibraryWebUrl);
    }
    if (!isEditing && currentCompany && !Bixby.Utils.isNullOrEmpty(currentCompany)) {
      setInitialFormValues({
        name: currentCompany.name,
        announcement: currentCompany.announcement || '',
        description: currentCompany.description || '',
        dataSiteName: currentCompany.dataSiteName,
        dataSiteHost_Id: currentCompany.dataSiteHost_Id,
        dataSiteProvider_Id: currentCompany.dataSiteProvider_Id,
        dataSiteType_Id: currentCompany.dataSiteType_Id,
        dataSiteStatus_Id: currentCompany.dataSiteStatus_Id,
        flagged: currentCompany.flagged,
        industry_Id: currentCompany.industry_Id,
        industryGroup_Id: currentCompany.industryGroup_Id,
        sector_Id: currentCompany.sector_Id,
        websiteUrl: currentCompany.websiteUrl || '',
        openFigiTicker: currentCompany.openFigiTicker || '',
        owners: currentCompany.owners || [],
        restrictNewsBackdate: currentCompany.restrictNewsBackdate,
        publicStockTicker: currentCompany.publicStockTicker,
        cikNumber: currentCompany.cikNumber,
        commitmentDate: currentCompany.commitmentDate,
        commitmentDateNotAvailable: currentCompany.commitmentDateNotAvailable,
        commitmentTime: currentCompany.commitmentTime,
        timezone: currentCompany.timezone,
        isDelisted: currentCompany.isDelisted,
        transactionTypes: currentCompany.transactionTypes,
        fitchIndustry_Id: currentCompany.fitchIndustry_Id,
        lfiId: currentCompany.lfiId,
      });
      setDateCreated(formatDate(currentCompany.createdDate));
    } else if (isEditing && !Bixby.Utils.isNullOrEmpty(draftCompany) && draftCompany) {
      setInitialFormValues({
        name: draftCompany.name,
        announcement: draftCompany.announcement || '',
        description: draftCompany.description || '',
        dataSiteName: draftCompany.dataSiteName,
        dataSiteHost_Id: draftCompany.dataSiteHost_Id,
        dataSiteProvider_Id: draftCompany.dataSiteProvider_Id,
        dataSiteStatus_Id: draftCompany.dataSiteStatus_Id,
        dataSiteType_Id: draftCompany.dataSiteType_Id,
        flagged: draftCompany.flagged,
        industry_Id: draftCompany.industry_Id,
        industryGroup_Id: draftCompany.industryGroup_Id,
        sector_Id: draftCompany.sector_Id,
        websiteUrl: draftCompany.websiteUrl || '',
        openFigiTicker: draftCompany.openFigiTicker || '',
        owners: draftCompany.owners || [],
        restrictNewsBackdate: draftCompany.restrictNewsBackdate,
        publicStockTicker: draftCompany.publicStockTicker,
        cikNumber: draftCompany.cikNumber,
        commitmentDate: draftCompany.commitmentDate,
        commitmentDateNotAvailable: draftCompany.commitmentDateNotAvailable,
        commitmentTime: draftCompany.commitmentTime,
        timezone: draftCompany.timezone,
        isDelisted: draftCompany.isDelisted,
        transactionTypes: draftCompany.transactionTypes,
        fitchIndustry_Id: draftCompany.fitchIndustry_Id,
        lfiId: draftCompany.lfiId,
      });
    }
  }, [isEditing, currentCompany, draftCompany, createdDate]);

  useEffect(() => {
    if (discardDraft) {
      handleResetForm();
    }
  }, [discardDraft]);

  const handleResetForm = () => {
    if (formikRef && formikRef.current) {
      formikRef.current.resetForm();
    }
  };

  const parser = new DOMParser();
  const setAnnouncement = (form: any, content: string) => {
    let { textContent } = parser.parseFromString(content, 'text/html').documentElement;
    if (textContent && Bixby.Utils.isDefined(textContent) && textContent?.trim()?.length <= 0) {
      textContent = '';
    } else {
      textContent = content;
    }
    form.setFieldValue('announcement', textContent);
  };

  const handleWatermarkLock = async (isWatermarkLocked: boolean) => {
    if (!currentCompany) return;

    await CompanyService.updateCompanyWatermarkStatus(currentCompany.id, isWatermarkLocked);
    setWatermarkConfirmationModal(false);
    await setCurrentCompany(currentCompany.id);
  };

  const openWatermarkConfirmationModal = () => {
    setWatermarkConfirmationModal(true);
  };

  const handleWatermarkConfirmationModalClose = () => {
    setWatermarkConfirmationModal(false);
  };

  // convert numeric values of PrivateEquitySponsors Type, which can be string or numeric
  // Backend returns string and internally on FE it is numeric
  const getNumericValue = (value: any) => {
    if (typeof PrivateEquitySponsorType[value] == 'number') return PrivateEquitySponsorType[value];

    return PrivateEquitySponsorType[PrivateEquitySponsorType[value]];
  };

  const websiteSchema = {
    websiteUrl: Yup.string()
      .nullable()
      .notRequired()
      .test('is-valid-url', 'Must be a valid URL', value => {
        if (!value) return true; // allow empty or null
        return Bixby.Constants.URL_REGEX.test(value) || Bixby.Constants.URL_REGEX.test('https://' + value);
      }),
  };

  const publicCompanySchema = Yup.object({
    ...websiteSchema,
  });

  const ownerSchema = Yup.object({
    owner_Id: Yup.string().max(256, 'Too long').required('Owner Id is required.'),
    percentage: Yup.number()
      .required('Percentage is required')
      .min(0, 'Percentage should not be less than 0')
      .max(100, 'Percentage cannot be greater than 100'),
    type: Yup.mixed().oneOf(Object.values(PrivateEquitySponsorType), 'Invalid type').required('Type is required'),
  });

  const schema = Yup.object({
    ...websiteSchema,
    owners: Yup.array()
      .of(ownerSchema)
      .test('owners', 'Total percentage cannot exceed 100', ownersList => {
        if (ownersList && ownersList?.length > 0) {
          const totalPercentage = ownersList?.reduce((sum, owner) => sum + owner.percentage, 0);
          return totalPercentage <= 100;
        } else return true;
      })
      .test('majoritytype', 'Only one sponsor can be tagged as Majority.', ownersList => {
        if (ownersList && ownersList.length > 0) {
          const majorityCount = ownersList.filter(o => o.type === PrivateEquitySponsorType.Majority).length;
          return majorityCount != 0 ? majorityCount === 1 : true;
        } else return true;
      })
      .test('majorityMoreThan50', 'Majority tag should have percentage more than 50', ownersList => {
        if (ownersList && ownersList?.length > 0) {
          const mojoritySponsor = ownersList.filter(_ => _.type === PrivateEquitySponsorType.Majority);
          const majorityCount = mojoritySponsor.length;

          return majorityCount != 0 ? mojoritySponsor[0].percentage > 50 : true;
        } else return true;
      })
      .test('minorityLessThan50', 'Minority tag should have percentage less than 50', ownersList => {
        if (ownersList && ownersList?.length > 0) {
          const minoritySponsor = ownersList.filter(_ => _.type === PrivateEquitySponsorType.Minority);
          const minorityCount = minoritySponsor.length;

          return minorityCount != 0 ? minoritySponsor.filter(_ => _.percentage >= 50).length === 0 : true;
        } else return true;
      })
      .test('majority_plurality', 'A company cannot be tagged with both Majority and Plurality.', ownersList => {
        if (ownersList && ownersList?.length > 0) {
          const majorityCount = ownersList.filter(o => o.type === PrivateEquitySponsorType.Majority).length;
          const pluralityCount = ownersList.filter(o => o.type === PrivateEquitySponsorType.Plurality).length;
          if (majorityCount != 0 || pluralityCount != 0) return majorityCount === 0 || pluralityCount === 0;
          else return true;
        } else return true;
      })
      .test('fiftyType', '50/50 splits should have exactly two sponsors.', ownersList => {
        if (ownersList && ownersList?.length > 0) {
          const fiftyFiftyCount = ownersList.filter(o => o.type === PrivateEquitySponsorType.FiftyFiftySplit).length;
          return fiftyFiftyCount != 0 ? fiftyFiftyCount == 2 && ownersList.length == 2 : true;
        } else return true;
      })
      .test('minorityType', 'The Plurality tag can only be used if there are more than two sponsors.', ownersList => {
        if (ownersList && ownersList?.length > 0) {
          const pluralityCount = ownersList.filter(o => o.type === PrivateEquitySponsorType.Plurality).length;
          return pluralityCount != 0 ? ownersList.length > 2 : true;
        } else return true;
      })
      .test('minorityType', 'All non-plurality sponsors should be tagged as Minority.', ownersList => {
        if (ownersList && ownersList?.length > 0) {
          const pluralityCount = ownersList.filter(o => o.type === PrivateEquitySponsorType.Plurality).length;
          const minorityCount = ownersList.filter(o => o.type === PrivateEquitySponsorType.Minority).length;
          return pluralityCount != 0
            ? pluralityCount == 1 && ownersList.length > 2 && minorityCount == ownersList.length - 1
            : true;
        } else return true;
      }),
  });

  return !Bixby.Utils.isNullOrEmpty(initialFormValues) ? (
    <div className="admin-profile-section bottom">
      <h3>Profile</h3>
      <Formik
        innerRef={formikRef}
        initialValues={initialFormValues}
        validationSchema={!isCurrentCompanyPublic ? schema : publicCompanySchema}
        enableReinitialize
        onSubmit={(values: CompanyProfileValues) => {
          const isSyndication: boolean =
            (values.dataSiteType_Id && values.dataSiteType_Id.toLocaleLowerCase()) ===
            Bixby.Constants.DATASITETYPE_SYNDICATION.toLocaleLowerCase();
          if (!isSyndication) {
            values.commitmentDateNotAvailable = false;
            values.commitmentDate = undefined;
            values.commitmentTime = undefined;
            values.timezone = undefined;
            values.transactionTypes = [];
          }

          const commandArray = Object.entries(values).map(field => {
            return {
              op: 'replace',
              path: `/${field[0]}`,
              value: field[1],
              from: '',
            } as Bixby.Models.JsonPatchOperation;
          });
          if (draftCompany) {
            dispatchUpdateDraftCompany(draftCompany.id, commandArray);
          }
        }}
        render={({ handleSubmit, values, setFieldValue, setFieldTouched }) => (
          <form onSubmit={handleSubmit}>
            <div className="profile-input-container">
              <div className="profile-input-column">
                <InputBlock title="Company Name">
                  <Field type="text" name="name" disabled={!isEditing} />
                </InputBlock>
                <InputBlock title="Bixby ID">
                  <input type="text" value={bixbyId} disabled />
                </InputBlock>
                {isCurrentCompanyPublic && (
                  <Field
                    name="isDelisted"
                    render={({ field }: { [key: string]: any }) => (
                      <BixCheckbox
                        id="isDelisted"
                        checked={field.value}
                        disabled={!isEditing}
                        title="Is the Company no longer listed."
                        onChange={field.onChange(field.name)}>
                        Defunct / Is No Longer Listed
                      </BixCheckbox>
                    )}
                  />
                )}
                <InputBlock title="Open FIGI Ticker">
                  <Field type="text" name="openFigiTicker" disabled={!isEditing} />
                </InputBlock>
                {isCurrentCompanyPublic && (
                  <>
                    <InputBlock title="Public Stock Ticker">
                      <Field type="text" name="publicStockTicker" disabled={!isEditing} />
                    </InputBlock>
                    <InputBlock title="CIK Number">
                      <Field type="text" name="cikNumber" disabled={!isEditing} />
                    </InputBlock>
                  </>
                )}
                <BixReadonlySelect
                  title="Data Site Provider"
                  fieldName="dataSiteProvider_Id"
                  fieldId={initialFormValues.dataSiteProvider_Id}
                  showDefaultOption={false}
                  isEditing={isCurrentCompanyPublic ? false : isEditing}
                  options={dataSiteProviders}
                />
                <BixReadonlySelect
                  title="Data Site Type"
                  fieldName="dataSiteType_Id"
                  fieldId={initialFormValues.dataSiteType_Id}
                  showDefaultOption={true}
                  isEditing={isCurrentCompanyPublic ? false : isEditing}
                  options={dataSiteTypes}
                />
                {values.dataSiteType_Id &&
                  values.dataSiteType_Id.toLocaleLowerCase() ===
                    Bixby.Constants.DATASITETYPE_SYNDICATION.toLocaleLowerCase() && (
                    <>
                      <InputBlock title="Timezone">
                        <Field
                          name="timezone"
                          render={({ form }: { [key: string]: any }) => {
                            return (
                              <select
                                defaultValue=""
                                required
                                onBlur={() => setFieldTouched('timezone')}
                                onChange={e => {
                                  form.setFieldValue('timezone', e.currentTarget.value);
                                }}
                                value={values.timezone}
                                disabled={!isEditing}>
                                <option hidden disabled value="" key="-1">
                                  Select timezone
                                </option>
                                {defaultTimezonesOptions}
                                <option disabled className="divider"></option>
                                {timezoneOptions}
                              </select>
                            );
                          }}
                        />
                      </InputBlock>
                      <InputBlock title="Commitment Date">
                        <DatePickerFieldWithHeader
                          dateOnly={true}
                          disableField={
                            <BixCheckbox
                              id="commitmentDateNotAvailable"
                              checked={values.commitmentDateNotAvailable}
                              disabled={!isEditing}
                              title="Not yet available"
                              onChange={event => {
                                setFieldValue('commitmentDateNotAvailable', event?.target?.checked);
                                if (event?.target?.checked) {
                                  setFieldValue('commitmentDate', null);
                                  setFieldValue('commitmentTime', null);
                                }
                              }}>
                              Not yet available
                            </BixCheckbox>
                          }
                          name="commitmentDate"
                          value={values.commitmentDate}
                          dateFormat="MMMM d, yyyy"
                          isClearable={isEditing}
                          showTimeSelect={false}
                          isEditing={isCurrentCompanyPublic ? false : isEditing}
                          disabled={!isEditing || values.commitmentDateNotAvailable}
                          onChange={(field: string, value: Date) => {
                            if (!value) {
                              setFieldValue(field, undefined);
                              return;
                            }

                            setFieldValue(field, value.toISOString());
                          }}
                        />
                      </InputBlock>
                      <InputBlock title="Commitment Time">
                        <TimePickerField
                          name="commitmentTime"
                          value={values.commitmentTime}
                          isClearable={isEditing}
                          isEditing={isCurrentCompanyPublic ? false : isEditing}
                          disabled={!isEditing || values.commitmentDateNotAvailable}
                          onChange={(field: string, value: Date) => {
                            if (!value) {
                              setFieldValue(field, undefined);
                              return;
                            }

                            setFieldValue(field, value.toISOString());
                          }}
                        />
                      </InputBlock>
                    </>
                  )}
                <InputBlock title="LFI Id">
                  <Field type="text" name="lfiId" disabled={!isEditing} />
                </InputBlock>
                {values.dataSiteType_Id &&
                  values.dataSiteType_Id.toLocaleLowerCase() ===
                    Bixby.Constants.DATASITETYPE_SYNDICATION.toLocaleLowerCase() && (
                    <BixReadonlyMultiselect
                      fieldName="transactionTypes"
                      isEditing={isCurrentCompanyPublic ? false : isEditing ?? false}
                      options={transactionTypes || []}
                      sort={true}
                      title="Transaction Types"
                    />
                  )}
                <BixReadonlySelect
                  title="Data Site Status"
                  fieldName="dataSiteStatus_Id"
                  fieldId={initialFormValues.dataSiteStatus_Id}
                  showDefaultOption={true}
                  isEditing={isCurrentCompanyPublic ? false : isEditing}
                  options={dataSiteStatuses}
                />
                <BixReadonlySelect
                  title="Data Site Host / Bank"
                  fieldName="dataSiteHost_Id"
                  fieldId={initialFormValues.dataSiteHost_Id}
                  showDefaultOption={true}
                  isEditing={isCurrentCompanyPublic ? false : isEditing}
                  options={dataSiteHosts}
                />
                <InputBlock title="Data Site Name">
                  <Field type="text" name="dataSiteName" disabled={isCurrentCompanyPublic ? true : !isEditing} />
                </InputBlock>
                <InputBlock title="Date Added">
                  <input className="date_added" type="text" value={dateCreated} disabled />
                </InputBlock>
                <InputBlock title="Announcement">
                  <Field
                    name="announcement"
                    disabled={!isEditing}
                    render={({ field, form }: { [key: string]: any }) => {
                      return (
                        <BixbyQuillWrapper
                          value={field.value}
                          onBlur={() => setAnnouncement(form, field.value)}
                          onChange={content => setAnnouncement(form, content)}
                          readOnly={!isEditing}
                        />
                      );
                    }}
                  />
                </InputBlock>
              </div>
              <div className="profile-input-column">
                {currentCompany && (
                  <>
                    <div className="document-lock-toggle">
                      {currentCompany.isWatermarkLocked ? (
                        <button
                          type="button"
                          className="document-lock-toggle"
                          title="Unlock Company for Watermarks"
                          onClick={() => openWatermarkConfirmationModal()}
                          disabled={!isDocHostingAdmin || isCurrentCompanyPublic}>
                          <LockIcon />
                          <span>Unlock Company for Watermarks</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="document-lock-toggle"
                          title="Lock Company for Watermarks"
                          onClick={() => openWatermarkConfirmationModal()}
                          disabled={!isDocHostingAdmin || isCurrentCompanyPublic}>
                          <UnlockIcon />
                          <span>Lock Company for Watermarks</span>
                        </button>
                      )}
                    </div>
                  </>
                )}
                {isSuperAdmin ? (
                  <section className="superadmin">
                    <Field
                      name="restrictNewsBackdate"
                      render={({ field }: { [key: string]: any }) => (
                        <BixCheckbox
                          id="restrictNewsBackdate"
                          checked={field.value}
                          disabled={!isEditing}
                          title="Restricts ability to backdate news and create email tasks."
                          onChange={field.onChange(field.name)}>
                          News: Restrict Backdate / Rogue Post Only
                        </BixCheckbox>
                      )}
                    />
                  </section>
                ) : null}

                <Field
                  name="flagged"
                  render={({ field }: { [key: string]: any }) => (
                    <BixCheckbox
                      id="flagged"
                      checked={field.value}
                      disabled={!isEditing}
                      title="Indicates this is a high-touch company."
                      onChange={field.onChange(field.name)}>
                      Company Flagged
                    </BixCheckbox>
                  )}
                />

                {currentCompany && currentCompany.hasDedicatedFinancials && (
                  <ExternalLink linkUrl={documentLibraryWebUrl} linkText="Financial Access URL (SharePoint)" />
                )}
                {currentCompany && !currentCompany.hasDedicatedFinancials && (
                  <button
                    className="create_dedicated_financials"
                    type="button"
                    onClick={() => setShowCreateDedicatedFinancialsModal(true)}>
                    Create Dedicated Financials
                  </button>
                )}

                <ExternalLink
                  linkUrl={documentLibraryWebUrl + '/admin/data-site'}
                  linkText="Data Site File URL (SharePoint)"
                />

                <InputBlock title="Description">
                  <Field name="description" component="textarea" disabled={!isEditing} />
                </InputBlock>
                <InputBlock title="Website">
                  <Field type="text" name="websiteUrl" disabled={!isEditing} />
                  <ErrorMessage name="websiteUrl">
                    {msg => <div className="error error-message">{msg}</div>}
                  </ErrorMessage>
                </InputBlock>

                {!isCurrentCompanyPublic && (
                  <InputBlock title="Ownership">
                    <Field
                      name="owners"
                      render={() => {
                        return (
                          <CompanyOwnership
                            isEditing={isEditing ?? false}
                            ownershipList={values.owners}
                            onChange={updatedList => {
                              const owners = updatedList.map(o => {
                                o.type = getNumericValue(o.type);
                                return o;
                              });
                              setFieldValue('owners', owners);
                            }}
                          />
                        );
                      }}
                    />
                  </InputBlock>
                )}

                {!isCurrentCompanyPublic && (
                  <ErrorMessage name="owners">
                    {msg => {
                      // In some circumstances without considering array would give error with blank screen of
                      // object not allowed as react child, mostly it would not be array in happy case
                      if (Array.isArray(msg)) {
                        return (
                          <>
                            {msg.map(m => (
                              <div className="error error-message">{m}</div>
                            ))}
                          </>
                        );
                      } else return <div className="error error-message">{msg}</div>;
                    }}
                  </ErrorMessage>
                )}

                <BixReadonlySelect
                  title="Sector"
                  fieldName="sector_Id"
                  fieldId={initialFormValues.sector_Id}
                  showDefaultOption={true}
                  isEditing={isEditing}
                  options={sectors}
                />
                <BixReadonlySelect
                  title="Industry Group"
                  fieldName="industryGroup_Id"
                  fieldId={initialFormValues.industryGroup_Id}
                  showDefaultOption={true}
                  isEditing={isEditing}
                  options={industryGroups}
                />
                <BixReadonlySelect
                  title="Industry"
                  fieldName="industry_Id"
                  fieldId={initialFormValues.industry_Id}
                  showDefaultOption={false}
                  isEditing={isEditing}
                  options={industries}
                />
                <BixReadonlySelect
                  required={false}
                  title="Fitch Industry"
                  fieldName="fitchIndustry_Id"
                  fieldId={initialFormValues.fitchIndustry_Id ?? ''}
                  showDefaultOption={true}
                  isEditing={isEditing}
                  options={fitchIndustries}
                />
              </div>
            </div>
            {isEditing && (
              <button className="bix_button" type="submit">
                Save as Draft
              </button>
            )}
          </form>
        )}></Formik>
      {watermarkConfirmationModal && (
        <Modal active={true} hideClose>
          <div className="reupload_modal_content">
            <>
              <p>
                Please confirm that you want to {currentCompany?.isWatermarkLocked ? 'unlock' : 'lock'} the company for
                watermarks.
              </p>
              <div className="modal_button_container">
                <BixButton
                  type="button"
                  onClick={() => handleWatermarkLock(currentCompany?.isWatermarkLocked ? false : true)}>
                  Confirm
                </BixButton>
                <BixButton light type="button" onClick={() => handleWatermarkConfirmationModalClose()}>
                  Cancel
                </BixButton>
              </div>
            </>
          </div>
        </Modal>
      )}
      {currentCompany && (
        <DedicatedFinancialsModal
          showCreateDedicatedFinancialsModal={showCreateDedicatedFinancialsModal}
          currentCompany={currentCompany}
          setCurrentCompany={setCurrentCompany}
          dispatchToastError={dispatchToastError}
          setShowCreateDedicatedFinancialsModal={setShowCreateDedicatedFinancialsModal}
        />
      )}
    </div>
  ) : null;
};
/**
 * TransformYupErrorsIntoObject
 *
 * @description Transform the useless yup error into a useable validation object
 * @param {ValidationError} errors Yup validation errors
 * @returns {Record<string, string>} Validation errors
 */
export const transformYupErrorsIntoObject = (errors: Yup.ValidationError): Record<string, string> => {
  const validationErrors: Record<string, string> = {};

  errors.inner.forEach((error: any) => {
    if (error.path !== undefined) {
      validationErrors[error.path] = error.errors[0];
    }
  });

  return validationErrors;
};

const mapStateToProps = (state: AppState) => {
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  const { isDocHostingAdmin } = getIsCurrentUserDocHostingAdminState(state);
  const { isCurrentCompanyPublic } = getIsCurrentCompanyPublicState(state);

  return {
    isSuperAdmin,
    isDocHostingAdmin,
    isCurrentCompanyPublic,
    isLoading: state.draftState.isLoading,
    error: state.draftState.error,
    draftCompany: state.draftState.draftCompany,
    isEditing: state.draftState.isEditing,
    discardDraft: state.draftState.discardDraft,
    currentCompany: state.companyState.currentCompany,
    industries: state.industryState.data,
    fitchIndustries: state.fitchIndustryState.data,
    industryGroups: state.industryGroupState.data,
    dataSiteHosts: state.dataSiteHostState.data,
    dataSiteStatuses: state.dataSiteStatusState.data,
    dataSiteProviders: state.dataSiteProviderState.data,
    dataSiteTypes: state.dataSiteTypeState.data,
    transactionTypes: state.companyTransactionTypeState.data,

    sectors: state.sectorState.data,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchUpdateDraftCompany: (companyId: string, draftCompanyCommand: Bixby.Models.JsonPatchOperation[]) =>
    dispatch(updateDraft(companyId, draftCompanyCommand)),
  setCurrentCompany: (companyId: string) => dispatch(setCurrentCompany(companyId)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyProfile);
